import { LanguageService, ParamsArg } from './language.service';
import { TranslationKey } from './translation-keys';

/**
 * DI-free translation access.
 *
 * `*.constants.ts` colDef closures (`valueGetter`, `valueFormatter`) run outside any injection
 * context, so they cannot `inject(LanguageService)`. They call these functions instead.
 *
 * Note this file imports the CONTRACT, never the engine — otherwise it would quietly become a
 * second Transloco importer and break the "one seam" property the abstraction exists for.
 */
let impl: LanguageService | null = null;

/** @internal Called once, by `TranslocoLanguageService`'s constructor. */
export function registerI18n(service: LanguageService): void {
  impl = service;
}

/**
 * Typed translate from a non-injectable context.
 *
 * The reactive dependency is registered inside the service (see `resolve()`), so a call made
 * during change detection — which is when a colDef closure runs — re-evaluates on a language
 * switch.
 *
 * Before the service exists (module evaluation, unit tests) this returns the key. It NEVER
 * caches: see the rule below.
 *
 * ⚠️ NEVER STORE THE RESULT. A translated string is a snapshot of the current language. Cache it
 * in a module constant, a `Map`, or a class field and you have frozen that language into the app
 * — the UI keeps rendering it after the user switches, and it looks like a reactivity bug three
 * components away. Worse, a module-level constant evaluates at IMPORT time, before the app has
 * resolved a language at all, so it freezes the raw KEY. Translate on every call.
 */
export function t<K extends TranslationKey>(key: K, ...args: ParamsArg<K>): string {
  return impl ? impl.t(key, ...args) : (key as string);
}

/**
 * Untyped, passthrough-tolerant translate for non-injectable code that receives a `string` it does
 * not own — a caller-supplied prefix, a value read from a config object. An unknown key returns
 * verbatim. Application code should prefer the typed `t()`; this is the DI-free twin of
 * `LanguageService.resolve()`, for the same framework-level cases.
 */
export function resolve(key: string, params?: Record<string, unknown>): string {
  return impl ? impl.resolve(key, params) : key;
}

/**
 * Resolve an enum key-map entry.
 *
 * The map's values are compile-checked as `TranslationKey` at the map's declaration site, so
 * indexing it dynamically here is safe — it cannot produce an unknown key.
 *
 * Returns `undefined` for unmapped values, so callers keep their `?? fallback`.
 */
export function tEnum<V extends PropertyKey>(
  map: Partial<Record<V, TranslationKey>>,
  value: V | null | undefined,
): string | undefined {
  const key = value == null ? undefined : map[value as V];

  if (!key) {
    return undefined;
  }

  return impl ? impl.resolve(key) : key;
}
